import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, ShieldCheck, Send, BadgeCheck, Archive, Mail, MessageSquare,
  Scale, LifeBuoy, PlayCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { date } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, EmptyState, PageHeader, Tabs,
} from '@/components/ui';
import { GettingStarted } from '@/components/GettingStarted';
import { FeedbackButton } from '@/components/Feedback';
import { ContactPROVIA } from '@/components/LiveChat';
import type {
  FeedbackReport, OnboardingState, SendingIdentity, FeedbackStatus,
} from '@shared/types';

/**
 * How the software works, and why it works that way.
 *
 * Three of PROVIA's decisions surprise people, and all three are deliberate:
 * nothing is sent without you pressing send, a payment is a claim until you
 * confirm it, and records are archived rather than deleted. A landlord who
 * does not know that thinks the first is broken, distrusts the second and is
 * frustrated by the third. Explaining them once, in the app, is cheaper than
 * answering them one email at a time.
 */

const STATUS_TONE: Record<FeedbackStatus, any> = {
  new: 'info',
  seen: 'neutral',
  planned: 'brand',
  done: 'ok',
  declined: 'neutral',
};

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: 'Received',
  seen: 'Read',
  planned: 'Planned',
  done: 'Done',
  declined: 'Not planned',
};

export default function Help() {
  const { staff } = useAuth();
  const [tab, setTab] = useState('start');

  const { data: onboarding, reload: reloadOnboarding } = useApi<OnboardingState>('/onboarding');
  const { data: reports } = useApi<FeedbackReport[]>('/feedback');
  const { data: settings } = useApi<{ sender: SendingIdentity }>('/settings');

  const reopen = async () => {
    await api.post('/onboarding', { dismissed: false });
    reloadOnboarding();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Help"
        subtitle="How PROVIA works, why it works that way, and how to tell us when it does not."
      />

      <Tabs
        tabs={[
          { id: 'start', label: 'Getting started' },
          { id: 'ideas', label: 'How it works' },
          { id: 'messaging', label: 'Messages and email' },
          { id: 'contact', label: 'Contact us' },
          { id: 'feedback', label: 'What you have told us' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'start' && (
        <div className="space-y-6">
          {onboarding?.dismissed || onboarding?.complete ? (
            <Card className="p-5">
              <div className="flex flex-wrap items-center gap-3">
                <BookOpen className="h-5 w-5 text-brand shrink-0" />
                <p className="text-sm flex-1 min-w-[240px]">
                  {onboarding.complete
                    ? 'Your account is fully set up. You can still walk back through the checklist.'
                    : 'The setup guide is hidden on your dashboard.'}
                </p>
                <Button size="sm" onClick={reopen}>Show it again</Button>
              </div>
            </Card>
          ) : null}

          <GettingStarted />

          <Card className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <PlayCircle className="h-5 w-5 text-brand shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h3 className="font-semibold">Want to try it without using real data?</h3>
                <p className="text-sm text-muted leading-relaxed">
                  Add one property, one room and one tenant with made-up details,
                  then run through a whole month: raise the rent, record a
                  payment as the tenant, verify it as the manager, and send a
                  reminder to yourself. It takes about ten minutes and it is the
                  fastest way to understand how the pieces fit together. Archive
                  the property afterwards — it drops out of every list and takes
                  nothing real with it.
                </p>
                <p className="text-sm text-muted leading-relaxed">
                  Use your own phone number and email address on the test tenant.
                  You will then see exactly what your tenants see, in the order
                  they see it.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'ideas' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Idea
            icon={<Send className="h-5 w-5" />}
            title="Nothing is sent without you"
            body={[
              'There is no scheduler. PROVIA never texts or emails a tenant on its own.',
              'The reminder queue works out who looks like they need chasing — overdue, due today, an extension running out, a tenancy ending, paperwork missing — and shows you the list. You choose who, you see the exact wording, and you press send.',
              'This is deliberate. Rent data is always slightly out of date: cash gets handed over on a doorstep, a transfer clears overnight, somebody was given until Friday verbally. Automatically texting "you are overdue" to a tenant who paid yesterday costs goodwill that is expensive to win back.',
              'The one exception is the portal invitation, which goes out when you add a tenant. It is true the moment it is written and says nothing about money — and you can switch it off per tenant.',
            ]}
          />
          <Idea
            icon={<BadgeCheck className="h-5 w-5" />}
            title="Claimed and confirmed are different"
            body={[
              'When a tenant says they have paid, that is a claim. It sits in your review queue with whatever proof they attached, and it does not move their balance.',
              'Balances are worked out from confirmed payments only. What is claimed is shown alongside, clearly labelled, on both sides of the app — so a tenant can see you have their receipt and you can see you have not checked it yet.',
              'The reminder queue also flags anyone whose payment is waiting on you, so you never chase somebody who is waiting on you.',
            ]}
          />
          <Idea
            icon={<Archive className="h-5 w-5" />}
            title="Deleting is mostly refused"
            body={[
              'A property with history is archived. A tenant with financial history is marked "past". A staff member is deactivated. A charge with money against it cannot be voided.',
              'That is not obstinacy — those records are your evidence if a tenancy is ever disputed, and a deposit argument two years from now is exactly when somebody wishes they had not tidied up.',
              'What you can always do is leave: Settings will delete the whole organisation, permanently, once you have typed its name.',
            ]}
          />
          <Idea
            icon={<ShieldCheck className="h-5 w-5" />}
            title="Your documents stay yours"
            body={[
              'Photos, contracts, IDs and receipts are kept in PROVIA Storage, in a folder tree that mirrors your portfolio.',
              'They are served through PROVIA’s own permission checks rather than as public links, so a tenant can only ever open what belongs to them.',
              'PROVIA Storage sits in storage you own, so if you stop using PROVIA the files stay exactly where they are.',
            ]}
          />
          <Card className="p-5 lg:col-span-2">
            <div className="flex items-start gap-3">
              <Scale className="h-5 w-5 text-warn shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h3 className="font-semibold">Staying on the right side of the law is your job</h3>
                <p className="text-sm text-muted leading-relaxed">
                  PROVIA is a record-keeping tool. It is not legal advice, and it
                  does not know the rules where you let. Deposit protection,
                  right-to-rent and immigration checks, gas and electrical safety
                  certificates, licensing, the notice you must give and the form
                  it must take, how long records must be kept, and what you may
                  do with a tenant&rsquo;s personal data are all your
                  responsibility, and they differ by country — and often by
                  council.
                </p>
                <p className="text-sm text-muted leading-relaxed">
                  The document checklist is there to help you keep track of what
                  you have decided you need. It is not a statement that those
                  documents are sufficient, or that they are the right ones. If
                  you are unsure, ask a solicitor or your local authority.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'messaging' && (
        <div className="space-y-4">
          <Card className="p-5 space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-brand shrink-0 mt-0.5" />
              <div className="space-y-2 min-w-0">
                <h3 className="font-semibold">Where your tenants&rsquo; replies go</h3>
                <p className="text-sm text-muted leading-relaxed">
                  Email from PROVIA goes out in your organisation&rsquo;s name,
                  from an address we host
                  {settings?.sender.email_from
                    ? <> — <span className="font-mono text-ink">{settings.sender.email_from}</span></>
                    : ' '}
                  . That address sends only; nothing is listening on it.
                </p>
                {settings?.sender.reply_to_header === false ? (
                  <p className="text-sm text-muted leading-relaxed">
                    Our mail provider cannot attach a reply-to address, so every
                    message names yours
                    {settings?.sender.reply_to
                      ? <> — <span className="font-mono text-ink">{settings.sender.reply_to}</span></>
                      : ''}
                    {' '}in the text itself and tells the tenant to write to it.
                    Nothing is forwarded and nothing is stored here; their reply
                    goes straight to you and never passes through PROVIA.
                  </p>
                ) : (
                  <p className="text-sm text-muted leading-relaxed">
                    Every message carries a reply-to of your own contact address
                    {settings?.sender.reply_to
                      ? <> — <span className="font-mono text-ink">{settings.sender.reply_to}</span></>
                      : ''}
                    , so a tenant who presses reply is writing straight to your
                    inbox. Nothing is forwarded and nothing is stored here,
                    because the reply never passes through PROVIA at all.
                  </p>
                )}
                <p className="text-sm text-muted leading-relaxed">
                  That is why a contact email address is required. Change it in{' '}
                  <Link to="/app/settings" className="text-brand hover:underline">Settings</Link>{' '}
                  and every message from then on replies to the new one.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-3">
            <div className="flex items-start gap-3">
              <MessageSquare className="h-5 w-5 text-brand shrink-0 mt-0.5" />
              <div className="space-y-2 min-w-0">
                <h3 className="font-semibold">Text messages</h3>
                <p className="text-sm text-muted leading-relaxed">
                  {settings?.sender.sms_from ? (
                    <>
                      Your texts go out from{' '}
                      <span className="font-mono text-ink">{settings.sender.sms_from}</span>,
                      which is yours. A tenant replying to a text reaches that
                      number.
                    </>
                  ) : (
                    <>
                      Texts currently go out from a shared number, so a tenant
                      replying to one will not reach you — the wording we send
                      says so, and points them at your phone number instead. Ask
                      us if you would like a number of your own.
                    </>
                  )}
                </p>
                <p className="text-sm text-muted leading-relaxed">
                  Every tenant can say how they want to hear from you — texts,
                  email, both or neither — on their record. A tenant who has
                  asked for email only is never texted, whichever template you
                  pick.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-3">
            <div className="flex items-start gap-3">
              <MessageSquare className="h-5 w-5 text-brand shrink-0 mt-0.5" />
              <div className="space-y-2 min-w-0">
                <h3 className="font-semibold">The portal conversation</h3>
                <p className="text-sm text-muted leading-relaxed">
                  Tenants can write to you from their account, and you can reply
                  from{' '}
                  <Link to="/app/messages" className="text-brand hover:underline">Messages</Link>.
                  Their message arrives in your inbox by email, with the text in
                  it, so you can decide whether it needs you without signing in.
                </p>
                <p className="text-sm text-muted leading-relaxed">
                  Your reply lives in their account. They are told it is waiting —
                  by email in full, or by a short text that does not quote it. A
                  conversation about arrears should not be sitting on a lock
                  screen where anyone can read it.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'contact' && (
        <div className="space-y-6 max-w-2xl">
          <ContactPROVIA />
        </div>
      )}

      {tab === 'feedback' && (
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex flex-wrap items-center gap-3">
              <LifeBuoy className="h-5 w-5 text-brand shrink-0" />
              <p className="text-sm flex-1 min-w-[240px]">
                Found a bug, or want PROVIA to do something it does not? Tell us.
                It goes to the people who build it.
              </p>
              <FeedbackButton
                className="w-auto bg-brand/12 text-brand hover:bg-brand/20 hover:text-brand"
                label="Report something"
              />
            </div>
          </Card>

          {!reports || reports.length === 0 ? (
            <EmptyState
              title="Nothing sent yet"
              message="Anything you or your colleagues report will be listed here, along with what came of it."
            />
          ) : (
            <Card className="p-0 overflow-hidden">
              <CardHeader title="What your organisation has reported" />
              <ul className="divide-y divide-line">
                {reports.map((report) => (
                  <li key={report.id} className="p-4 sm:p-5 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={STATUS_TONE[report.status]}>
                        {STATUS_LABEL[report.status]}
                      </Badge>
                      <span className="text-sm font-medium">{report.subject}</span>
                      <span className="text-xs text-faint ml-auto">
                        {report.author_label} · {date(report.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-muted whitespace-pre-wrap">{report.body}</p>
                    {report.operator_note && (
                      <p className="text-sm border-l-2 border-brand/40 pl-3 text-ink">
                        {report.operator_note}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {staff?.role === 'owner' && (
            <p className="text-xs text-faint">
              Everyone in your organisation can report something. Staff see every
              report from your organisation; tenants see only their own.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Idea({ icon, title, body }: { icon: any; title: string; body: string[] }) {
  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <span className="shrink-0 h-9 w-9 rounded-xl bg-brand/12 text-brand flex items-center justify-center">
          {icon}
        </span>
        <div className="space-y-2 min-w-0">
          <h3 className="font-semibold">{title}</h3>
          {body.map((paragraph, i) => (
            <p key={i} className="text-sm text-muted leading-relaxed">{paragraph}</p>
          ))}
        </div>
      </div>
    </Card>
  );
}
